/**
 * Google Local Services Ads (LSA) API Client
 * Manages LSA leads, disputes, and performance data
 * 
 * Note: Google doesn't provide a public LSA API yet, so this client provides
 * methods for manual management and webhook-based integration.
 */

import { supabase } from './supabase';

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface LSALead {
  id: string;
  google_lead_id: string;
  customer_name: string;
  customer_phone: string;
  service_type: string;
  charge_amount: number;
  status: string;
  created_at: string;
}

export interface LSAPerformanceMetrics {
  period_start: string;
  period_end: string;
  total_leads: number;
  total_cost: number;
  booked_leads: number;
  booking_rate: number;
  avg_response_time: number;
  cost_per_booked_job: number;
  roi: number;
}

export interface DisputeParams {
  lead_id: string;
  reason: 'wrong_service' | 'unreachable' | 'duplicate' | 'spam' | 'out_of_area' | 'commercial';
  description: string;
  evidence_urls?: string[];
}

export interface BudgetStatus {
  daily_budget: number;
  daily_spend: number;
  daily_usage_pct: number;
  weekly_budget: number;
  weekly_spend: number;
  monthly_budget: number;
  monthly_spend: number;
  is_paused: boolean;
}

// ============================================================================
// Google LSA Client
// ============================================================================

export class GoogleLSAClient {
  /**
   * Get all LSA leads for a company
   */
  async getLeads(
    companyId: string,
    filters?: {
      status?: string;
      service_type?: string;
      date_from?: string;
      date_to?: string;
      limit?: number;
    }
  ): Promise<LSALead[]> {
    try {
      let query = supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      if (filters?.service_type) {
        query = query.eq('service_type', filters.service_type);
      }

      if (filters?.date_from) {
        query = query.gte('created_at', filters.date_from);
      }

      if (filters?.date_to) {
        query = query.lte('created_at', filters.date_to);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching LSA leads:', error);
      return [];
    }
  }

  /**
   * Get a single lead by ID
   */
  async getLeadDetails(leadId: string): Promise<LSALead | null> {
    try {
      const { data, error } = await supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('id', leadId)
        .single();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error('Error fetching lead details:', error);
      return null;
    }
  }

  /**
   * Update lead status
   */
  async updateLeadStatus(
    leadId: string,
    status: 'new' | 'contacted' | 'qualified' | 'booked' | 'invalid' | 'disputed'
  ): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('google_lsa_leads')
        .update({ status })
        .eq('id', leadId);

      if (error) throw error;

      console.log(`✅ Lead ${leadId} status updated to: ${status}`);
      return true;
    } catch (error) {
      console.error('Error updating lead status:', error);
      return false;
    }
  }

  /**
   * File a dispute for an invalid lead
   */
  async fileDispute(params: DisputeParams, companyId: string, filedBy: string): Promise<string | null> {
    try {
      // Get the lead
      const { data: lead } = await supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('google_lead_id', params.lead_id)
        .eq('company_id', companyId)
        .single();

      if (!lead) {
        console.error('Lead not found');
        return null;
      }

      // Create dispute record
      const { data: dispute, error } = await supabase
        .from('lsa_disputes')
        .insert({
          lsa_lead_id: lead.id,
          company_id: companyId,
          dispute_reason: params.reason,
          dispute_description: params.description,
          evidence_urls: params.evidence_urls || [],
          filed_by: filedBy,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;

      // Update lead status
      await supabase
        .from('google_lsa_leads')
        .update({
          status: 'disputed',
          dispute_status: 'pending',
          dispute_reason: params.description,
          dispute_filed_at: new Date().toISOString(),
        })
        .eq('id', lead.id);

      console.log(`✅ Dispute filed for lead ${params.lead_id}`);
      return dispute.id;
    } catch (error) {
      console.error('Error filing dispute:', error);
      return null;
    }
  }

  /**
   * Get performance metrics for a date range
   * Now includes true CPBJ (Cost Per Completed Job) from Successware data
   */
  async getPerformanceMetrics(
    companyId: string,
    dateRange: { start: string; end: string }
  ): Promise<LSAPerformanceMetrics | null> {
    try {
      const { data: leads } = await supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('company_id', companyId)
        .gte('created_at', dateRange.start)
        .lte('created_at', dateRange.end);

      if (!leads || leads.length === 0) {
        return null;
      }

      const totalLeads = leads.length;
      const totalCost = leads.reduce((sum, l) => sum + (l.charge_amount || 0), 0);
      
      // Booked jobs (from Contact Center outcome)
      const bookedLeads = leads.filter(l => 
        l.booking_success || 
        l.status === 'booked' || 
        l.contact_center_outcome === 'booked'
      );
      const bookedJobs = bookedLeads.length;
      
      // Completed jobs (from Successware - TRUE CPBJ)
      const completedLeads = leads.filter(l => l.job_status === 'completed');
      const completedJobs = completedLeads.length;
      
      // Revenue (from completed jobs with actual invoice values)
      const totalRevenue = completedLeads.reduce((sum, l) => sum + (l.job_value || 0), 0);
      
      // Response time metrics
      const responseTimes = leads
        .filter(l => l.response_time_seconds !== null)
        .map(l => l.response_time_seconds);
      const avgResponseTime = responseTimes.length > 0
        ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
        : 0;

      // Contact Center metrics
      const missedCalls = leads.filter(l => l.contact_center_outcome === 'missed').length;
      const respondedUnder60 = leads.filter(l => 
        l.response_time_seconds && l.response_time_seconds <= 60
      ).length;
      const respondedUnder300 = leads.filter(l => 
        l.response_time_seconds && l.response_time_seconds <= 300
      ).length;

      // CPBJ calculations
      const costPerBookedJob = bookedJobs > 0 ? totalCost / bookedJobs : 0;
      const costPerCompletedJob = completedJobs > 0 ? totalCost / completedJobs : 0; // TRUE CPBJ
      
      const roi = totalCost > 0 ? totalRevenue / totalCost : 0;
      const bookingRate = totalLeads > 0 ? (bookedJobs / totalLeads) * 100 : 0;
      const completionRate = bookedJobs > 0 ? (completedJobs / bookedJobs) * 100 : 0;

      return {
        period_start: dateRange.start,
        period_end: dateRange.end,
        total_leads: totalLeads,
        total_cost: totalCost,
        booked_leads: bookedJobs,
        booking_rate: bookingRate,
        avg_response_time: Math.round(avgResponseTime),
        cost_per_booked_job: costPerBookedJob,
        roi,
        // Extended metrics (new)
        completed_jobs: completedJobs,
        completion_rate: completionRate,
        cost_per_completed_job: costPerCompletedJob, // TRUE CPBJ
        total_revenue: totalRevenue,
        missed_calls: missedCalls,
        responded_under_60s: respondedUnder60,
        responded_under_5min: respondedUnder300,
        answer_rate: totalLeads > 0 ? ((totalLeads - missedCalls) / totalLeads) * 100 : 100,
      } as any; // Extended beyond base interface
    } catch (error) {
      console.error('Error getting performance metrics:', error);
      return null;
    }
  }

  /**
   * Get responsiveness score for a date
   */
  async getResponsivenessScore(companyId: string, date?: string): Promise<number> {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];

      const { data } = await supabase
        .from('lsa_responsiveness_log')
        .select('responsiveness_score')
        .eq('company_id', companyId)
        .eq('report_date', targetDate)
        .single();

      return data?.responsiveness_score || 0;
    } catch (error) {
      console.error('Error getting responsiveness score:', error);
      return 0;
    }
  }

  /**
   * Calculate responsiveness score based on response times
   */
  async calculateResponsivenessScore(companyId: string, date: string): Promise<number> {
    try {
      const { data: log } = await supabase
        .from('lsa_responsiveness_log')
        .select('*')
        .eq('company_id', companyId)
        .eq('report_date', date)
        .single();

      if (!log || log.total_leads_received === 0) {
        return 100; // No leads = perfect score
      }

      // Google's scoring formula (estimated):
      // - 100% if all leads responded within 1 hour
      // - 85% if all leads responded within 3 hours
      // - 70% if all leads responded within 24 hours
      // - Penalty for each missed lead

      const response1hRate = log.leads_responded_1h / log.total_leads_received;
      const response3hRate = log.leads_responded_3h / log.total_leads_received;
      const response24hRate = log.leads_responded_24h / log.total_leads_received;

      let score = 0;

      if (response1hRate >= 0.9) {
        score = 100; // Elite
      } else if (response3hRate >= 0.8) {
        score = 85; // Standard
      } else if (response24hRate >= 0.7) {
        score = 70; // Acceptable
      } else {
        score = response24hRate * 70; // Proportional penalty
      }

      // Update the log with calculated score
      await supabase
        .from('lsa_responsiveness_log')
        .update({
          responsiveness_score: score,
          response_rate_24h: response24hRate * 100,
          response_rate_1h: response1hRate * 100,
          google_badge_status: score >= 90 ? 'elite' : score >= 75 ? 'standard' : score >= 60 ? 'at_risk' : 'suspended',
          needs_attention: score < 75,
        })
        .eq('company_id', companyId)
        .eq('report_date', date);

      return score;
    } catch (error) {
      console.error('Error calculating responsiveness score:', error);
      return 0;
    }
  }

  /**
   * Get budget status
   */
  async getBudgetStatus(companyId: string): Promise<BudgetStatus | null> {
    try {
      const { data } = await supabase
        .from('lsa_budget_status')
        .select('*')
        .eq('company_id', companyId)
        .single();

      return data;
    } catch (error) {
      console.error('Error getting budget status:', error);
      return null;
    }
  }

  /**
   * Update budget configuration
   */
  async updateBudget(
    companyId: string,
    budgets: {
      daily?: number;
      weekly?: number;
      monthly?: number;
    }
  ): Promise<boolean> {
    try {
      const updates: any = {};
      if (budgets.daily !== undefined) updates.daily_budget = budgets.daily;
      if (budgets.weekly !== undefined) updates.weekly_budget = budgets.weekly;
      if (budgets.monthly !== undefined) updates.monthly_budget = budgets.monthly;

      const { error } = await supabase
        .from('lsa_budget_config')
        .update(updates)
        .eq('company_id', companyId);

      if (error) throw error;

      console.log('✅ Budget configuration updated');
      return true;
    } catch (error) {
      console.error('Error updating budget:', error);
      return false;
    }
  }

  /**
   * Reset budget spend (used for daily/weekly/monthly resets)
   */
  async resetBudgetSpend(
    companyId: string,
    period: 'daily' | 'weekly' | 'monthly'
  ): Promise<void> {
    try {
      const updates: any = {};
      const now = new Date().toISOString();

      switch (period) {
        case 'daily':
          updates.daily_spend = 0;
          updates.last_daily_reset = now;
          updates.service_spend = {}; // Reset service-specific spend
          break;
        case 'weekly':
          updates.weekly_spend = 0;
          updates.last_weekly_reset = now;
          break;
        case 'monthly':
          updates.monthly_spend = 0;
          updates.last_monthly_reset = now;
          break;
      }

      // Auto-resume if configured
      updates.is_paused = false;
      updates.pause_reason = null;

      await supabase
        .from('lsa_budget_config')
        .update(updates)
        .eq('company_id', companyId);

      console.log(`✅ ${period} budget reset for company ${companyId}`);
    } catch (error) {
      console.error(`Error resetting ${period} budget:`, error);
    }
  }

  /**
   * Get service performance breakdown
   */
  async getServicePerformance(
    companyId: string,
    periodType: 'daily' | 'weekly' | 'monthly' = 'monthly'
  ): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('lsa_service_performance')
        .select('*')
        .eq('company_id', companyId)
        .eq('period_type', periodType)
        .order('period_start', { ascending: false })
        .limit(30);

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting service performance:', error);
      return [];
    }
  }

  /**
   * Get dispute history
   */
  async getDisputes(companyId: string, status?: string): Promise<any[]> {
    try {
      let query = supabase
        .from('lsa_disputes')
        .select(`
          *,
          google_lsa_leads (
            google_lead_id,
            customer_name,
            customer_phone,
            service_type,
            charge_amount
          )
        `)
        .eq('company_id', companyId)
        .order('filed_at', { ascending: false });

      if (status) {
        query = query.eq('status', status);
      }

      const { data, error } = await query;

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting disputes:', error);
      return [];
    }
  }

  /**
   * Update dispute status
   */
  async updateDisputeStatus(
    disputeId: string,
    status: 'pending' | 'under_review' | 'approved' | 'denied' | 'withdrawn',
    outcome?: {
      refund_amount?: number;
      google_response?: string;
    }
  ): Promise<boolean> {
    try {
      const updates: any = { status };

      if (status === 'approved' || status === 'denied') {
        updates.resolved_at = new Date().toISOString();
      }

      if (outcome) {
        if (outcome.refund_amount !== undefined) {
          updates.refund_amount = outcome.refund_amount;
          updates.outcome = outcome.refund_amount > 0 ? 'full_refund' : 'no_refund';
        }
        if (outcome.google_response) {
          updates.google_response = outcome.google_response;
        }
      }

      const { error } = await supabase
        .from('lsa_disputes')
        .update(updates)
        .eq('id', disputeId);

      if (error) throw error;

      console.log(`✅ Dispute ${disputeId} updated to: ${status}`);
      return true;
    } catch (error) {
      console.error('Error updating dispute:', error);
      return false;
    }
  }

  /**
   * Get today's summary
   */
  async getTodaySummary(companyId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('lsa_today_summary')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) throw error;

      return data || {
        total_leads: 0,
        total_cost: 0,
        new_leads: 0,
        contacted_leads: 0,
        booked_leads: 0,
        avg_response_time: 0,
        avg_quality_score: 0,
      };
    } catch (error) {
      console.error('Error getting today summary:', error);
      return null;
    }
  }

  /**
   * Get responsiveness trend (last 30 days)
   */
  async getResponsivenessTrend(companyId: string, days: number = 30): Promise<any[]> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const { data, error } = await supabase
        .from('lsa_responsiveness_log')
        .select('*')
        .eq('company_id', companyId)
        .gte('report_date', startDate.toISOString().split('T')[0])
        .order('report_date', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting responsiveness trend:', error);
      return [];
    }
  }

  /**
   * Get leads needing urgent response
   */
  async getUrgentLeads(companyId: string): Promise<any[]> {
    try {
      // Get leads created in last 24 hours with no response
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      const { data, error } = await supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('company_id', companyId)
        .eq('status', 'new')
        .gte('created_at', yesterday.toISOString())
        .is('first_contact_at', null)
        .order('created_at', { ascending: true });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error getting urgent leads:', error);
      return [];
    }
  }

  /**
   * Calculate statistics for dashboard
   */
  async getDashboardStats(companyId: string): Promise<any> {
    try {
      const today = new Date().toISOString().split('T')[0];
      const monthStart = new Date();
      monthStart.setDate(1);

      // Get today's leads
      const { data: todayLeads } = await supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('company_id', companyId)
        .gte('created_at', `${today}T00:00:00`);

      // Get month's leads
      const { data: monthLeads } = await supabase
        .from('google_lsa_leads')
        .select('*')
        .eq('company_id', companyId)
        .gte('created_at', monthStart.toISOString());

      // Get budget status
      const budgetStatus = await this.getBudgetStatus(companyId);

      // Get responsiveness score
      const responsivenessScore = await this.getResponsivenessScore(companyId, today);

      return {
        today: {
          total: todayLeads?.length || 0,
          booked: todayLeads?.filter(l => l.booking_success).length || 0,
          cost: todayLeads?.reduce((sum, l) => sum + (l.charge_amount || 0), 0) || 0,
        },
        month: {
          total: monthLeads?.length || 0,
          booked: monthLeads?.filter(l => l.booking_success).length || 0,
          cost: monthLeads?.reduce((sum, l) => sum + (l.charge_amount || 0), 0) || 0,
          revenue: monthLeads?.filter(l => l.booking_success).reduce((sum, l) => sum + (l.job_value || 0), 0) || 0,
        },
        budget: budgetStatus,
        responsiveness_score: responsivenessScore,
      };
    } catch (error) {
      console.error('Error getting dashboard stats:', error);
      return null;
    }
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

let lsaClientInstance: GoogleLSAClient | null = null;

export function getLSAClient(): GoogleLSAClient {
  if (!lsaClientInstance) {
    lsaClientInstance = new GoogleLSAClient();
  }
  return lsaClientInstance;
}

/**
 * Helper function to get leads for a specific period
 */
export async function getLSALeads(
  companyId: string,
  period: 'today' | 'week' | 'month' = 'month'
): Promise<LSALead[]> {
  const client = getLSAClient();
  
  const now = new Date();
  let dateFrom: string;

  switch (period) {
    case 'today':
      dateFrom = new Date().toISOString().split('T')[0] + 'T00:00:00';
      break;
    case 'week': {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      dateFrom = weekAgo.toISOString();
      break;
    }
    case 'month': {
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      dateFrom = monthStart.toISOString();
      break;
    }
  }

  return await client.getLeads(companyId, { date_from: dateFrom });
}
